import { useEffect, useState } from 'react';
import { SIGNATURE_FONTS, SignatureFont } from './constants';

// Fonts are only needed once a signer opens the Type tab or a typed signature is shown, so load them lazily.
const FONT_LOADERS: Record<SignatureFont, () => Promise<unknown>> = {
  'Kaushan Script': () => import('@fontsource/kaushan-script/400.css'),
  'Nothing You Could Do': () => import('@fontsource/nothing-you-could-do/400.css'),
  Caveat: () => import('@fontsource/caveat/400.css'),
  'Fuzzy Bubbles': () => import('@fontsource/fuzzy-bubbles/400.css'),
};

const pending = new Map<SignatureFont, Promise<void>>();

export const loadSignatureFont = (font: SignatureFont): Promise<void> => {
  const existing = pending.get(font);
  if (existing) return existing;

  const promise = FONT_LOADERS[font]()
    .then(() => document.fonts?.load(`400 32px "${font}"`))
    .then(() => undefined)
    .catch(() => {
      // Falls back to the cursive system font; allow a later retry.
      pending.delete(font);
    });
  pending.set(font, promise);
  return promise;
};

/** Returns true once the requested fonts are ready (or failed and fell back). */
export const useSignatureFonts = (fonts: readonly SignatureFont[] = SIGNATURE_FONTS, enabled = true) => {
  const [isReady, setIsReady] = useState(false);
  const fontsKey = fonts.join('|');

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    Promise.all(fonts.map(loadSignatureFont)).then(() => {
      if (!cancelled) setIsReady(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontsKey, enabled]);

  return isReady;
};
